import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Spin } from 'antd';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Community from './pages/Community';
import Profile from './pages/Profile';
import Messages from './pages/Messages';
// 保持原有功能页面兼容（Agent协作等）
import AICreator from './pages/AICreator';
import AgentCrew from './pages/AgentCrew';
import LifeScenario from './pages/LifeScenario';
import LifePage from './pages/LifePage';
import AICommandCenter from './pages/AICommandCenter';
import MemoryCenter from './pages/MemoryCenter';
import OpportunityRadar from './pages/OpportunityRadar';
import Shopping from './pages/Shopping';
import Services from './pages/Services';
import Business from './pages/Business';
import Influencer from './pages/Influencer';
import GEO from './pages/GEO';
import ApprovalCenter from './pages/ApprovalCenter';
import ABTest from './pages/ABTest';
import TeamPage from './pages/Team';

// SIMON AI OS — 核心模块
import DecisionWorkspace from './pages/DecisionWorkspace';
import ExecutionCenter from './pages/ExecutionCenter';
import MonitorRuntime from './pages/MonitorRuntime';
import KnowledgeBase from './pages/KnowledgeBase';
import AgentOrchestration from './pages/AgentOrchestration';
import AgentExecutionPage from './pages/agent-platform/AgentExecutionPage';
import Marketing from './pages/Marketing';
import DesignSystem from './pages/DesignSystem';
import Marketplace from './pages/Marketplace';
import HomePage from './pages/HomePage';

// SimonCat Agent 平台 — 产品化入口
import AgentPlatformLayout from './pages/agent-platform/AgentPlatformLayout';
import AgentHomePage from './pages/agent-platform/AgentHomePage';
import AgentListPage from './pages/agent-platform/AgentListPage';
import AgentMarketplacePage from './pages/agent-platform/AgentMarketplacePage';
import ExecutionCenterPage from './pages/agent-platform/ExecutionCenterPage';
import KnowledgePage from './pages/agent-platform/KnowledgePage';
import ModelCenterPage from './pages/agent-platform/ModelCenterPage';
import WorkflowsPage from './pages/agent-platform/WorkflowsPage';
import NewAgentPage from './pages/agent-platform/NewAgentPage';

// Auth 页面
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPage from './pages/auth/ForgotPage';

// Postiz 功能页面 - 懒加载
const ContentCalendar = lazy(() => import('./pages/ContentCalendar'));
const Launches = lazy(() => import('./pages/Launches'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Media = lazy(() => import('./pages/Media'));
const Agents = lazy(() => import('./pages/IntelligentAgent'));
const Settings = lazy(() => import('./pages/Settings'));
const Billing = lazy(() => import('./pages/BillingPage'));
const Plugs = lazy(() => import('./pages/PlugsPage'));
const ThirdParty = lazy(() => import('./pages/ThirdPartyPage'));

const RequireAuth = ({ children }) => {
  // 开发模式：跳过登录验证，直接放行
  return children;
};

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

const withSuspense = (el) => <Suspense fallback={<Loading />}>{el}</Suspense>;

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth 页面 - 无布局 */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/forgot" element={<ForgotPage />} />
        <Route path="/auth" element={<Navigate to="/login" replace />} />

        {/* 首页 — 萤火秘境着陆页 */}
        <Route path="/" element={<HomePage />} />
        <Route path="/life-scenario" element={<LifeScenario />} />

        {/* 主应用布局 — 嵌套路由 */}
        <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="community" element={<Community />} />
          <Route path="projects" element={<Projects />} />
          <Route path="projects/:id" element={<Projects />} />
          <Route path="messages" element={<Messages />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={withSuspense(<Settings />)} />
          {/* SIMON AI OS 核心模块 — 在 MainLayout 内 */}
          <Route path="ai-os" element={<AICommandCenter />} />
          <Route path="decision" element={<DecisionWorkspace />} />
          <Route path="execution" element={<ExecutionCenter />} />
          <Route path="monitor" element={<MonitorRuntime />} />
          <Route path="agent-orchestration" element={<AgentOrchestration />} />
          <Route path="agents/execute" element={<AgentExecutionPage />} />
          <Route path="design-system" element={<DesignSystem />} />
          <Route path="opportunity-radar" element={<OpportunityRadar />} />
          <Route path="life" element={<LifePage />} />
          <Route path="shopping" element={<Shopping />} />
          <Route path="services" element={<Services />} />
          <Route path="business" element={<Business />} />
          <Route path="knowledge-base" element={<KnowledgeBase />} />
          <Route path="memory" element={<MemoryCenter />} />
          {/* 审批中心 — 人在回路 */}
          <Route path="approval-center" element={<ApprovalCenter />} />
          {/* 保持原有功能 */}
          <Route path="ai" element={<AICreator />} />
          <Route path="ai-creator" element={<AICreator />} />
          <Route path="agent-crew" element={<AgentCrew />} />
          {/* Postiz 社交媒体管理功能 */}
          <Route path="content-calendar" element={withSuspense(<ContentCalendar />)} />
          <Route path="launches" element={withSuspense(<Launches />)} />
          <Route path="analytics" element={withSuspense(<Analytics />)} />
          <Route path="media" element={withSuspense(<Media />)} />
          <Route path="agents" element={withSuspense(<Agents />)} />
          <Route path="billing" element={withSuspense(<Billing />)} />
          <Route path="plugs" element={withSuspense(<Plugs />)} />
          <Route path="third-party" element={withSuspense(<ThirdParty />)} />
          {/* 达人营销Agent + GEO生成式引擎优化 */}
          <Route path="influencer" element={<Influencer />} />
          <Route path="geo" element={<GEO />} />
          <Route path="ab-test" element={<ABTest />} />
          <Route path="team" element={<TeamPage />} />
          {/* Agent 技能市场 */}
          <Route path="marketplace" element={<Marketplace />} />
        {/* 营销效果闭环分析页面 */}
        <Route path="marketing" element={<Marketing />} />
        </Route>

        {/* SimonCat Agent 平台 — 嵌套路由（侧边栏布局 + Outlet） */}
        <Route path="/simon-cat/*" element={<RequireAuth><AgentPlatformLayout /></RequireAuth>}>
          <Route index element={<AgentHomePage />} />
          <Route path="agents" element={<AgentListPage />} />
          <Route path="agents/new" element={<NewAgentPage />} />
          <Route path="agents/:id" element={<AgentExecutionPage />} />
          <Route path="marketplace" element={<AgentMarketplacePage />} />
          <Route path="execution" element={<ExecutionCenterPage />} />
          <Route path="knowledge" element={<KnowledgePage />} />
          <Route path="models" element={<ModelCenterPage />} />
          <Route path="workflows" element={<WorkflowsPage />} />
        </Route>


        {/* 404 重定向到首页 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
